/**
 * The API client and the tiny router.
 *
 * No data-fetching library: this dashboard has nine endpoints, all GET, all
 * read-only, and the useful behaviour (poll, show stale data while refetching,
 * surface the error instead of an empty page) is ~60 lines. A library would be
 * larger than the thing it replaced.
 *
 * `useApi` deliberately KEEPS the previous payload while a refetch is in flight.
 * A monitoring dashboard that blanks itself every 30 seconds is unusable, and a
 * dashboard that blanks itself when the network hiccups actively misleads —
 * "nothing here" and "could not reach the server" must look different.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, {
    signal: signal ?? null,
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
  });
  const text = await response.text();
  let body: unknown = null;
  if (text !== '') {
    try {
      body = JSON.parse(text);
    } catch {
      // A non-JSON body from a JSON endpoint is itself the diagnosis (a proxy
      // error page, an HTML login redirect). Carry it rather than swallow it.
      body = text;
    }
  }
  if (!response.ok) {
    const detail =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${String(response.status)}`;
    throw new ApiError(response.status, body, detail);
  }
  return body as T;
}

export async function apiPost<T>(path: string, payload: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });
  const text = await response.text();
  const body: unknown = text === '' ? null : JSON.parse(text);
  if (!response.ok) {
    const detail =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${String(response.status)}`;
    throw new ApiError(response.status, body, detail);
  }
  return body as T;
}

export interface ApiState<T> {
  data: T | null;
  error: ApiError | Error | null;
  loading: boolean;
  /** True while refetching with data already on screen. */
  refreshing: boolean;
  reload: () => void;
  lastLoadedAt: Date | null;
}

export function useApi<T>(path: string | null, pollMs = 0): ApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const hasData = useRef(false);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    if (hasData.current) setRefreshing(true);
    else setLoading(true);

    apiGet<T>(path, controller.signal)
      .then((payload) => {
        if (cancelled) return;
        hasData.current = true;
        setData(payload);
        setError(null);
        setLastLoadedAt(new Date());
      })
      .catch((err: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        // The previous payload stays on screen; the error is shown alongside it,
        // stamped, so "stale but readable" is distinguishable from "fresh".
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [path, nonce]);

  useEffect(() => {
    if (pollMs <= 0 || path === null) return;
    const handle = setInterval(reload, pollMs);
    return () => {
      clearInterval(handle);
    };
  }, [pollMs, path, reload]);

  return { data, error, loading, refreshing, reload, lastLoadedAt };
}

/* ------------------------------------------------------------------ *
 * Router — history API, ~40 lines, no dependency
 * ------------------------------------------------------------------ */

export function useRoute(): { path: string; navigate: (to: string) => void } {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = (): void => {
      setPath(window.location.pathname);
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  const navigate = useCallback((to: string) => {
    if (to === window.location.pathname) return;
    window.history.pushState({}, '', to);
    setPath(to);
    window.scrollTo(0, 0);
  }, []);

  return { path, navigate };
}
